import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Chip,
  Collapse,
  Dialog,
  DialogHeader,
  DialogBody,
  IconButton,
} from '@material-tailwind/react';
import {
  SparklesIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

import { getDefaultGuidelines } from '../../services/reportApi';

function AISettingsPanel({ draft, onGuidelinesChange, onHasChanges, onError, resetKey, reportType }) {
  const originalGuidelines = draft?.customGuidelines || '';

  const [guidelines, setGuidelines] = useState(originalGuidelines);
  const [defaultGuidelines, setDefaultGuidelines] = useState('');
  const [showPass1Prompt, setShowPass1Prompt] = useState(false);
  const [showPass2Prompt, setShowPass2Prompt] = useState(false);
  const [showLegacyPrompt, setShowLegacyPrompt] = useState(false);
  const [showPass1Dialog, setShowPass1Dialog] = useState(false);
  const [showPass2Dialog, setShowPass2Dialog] = useState(false);
  const [showLegacyDialog, setShowLegacyDialog] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(null);

  const handleCopyPrompt = async (promptType, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPrompt(promptType);
      setTimeout(() => setCopiedPrompt(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Check if we have new prompts or need to fall back to legacy
  const hasNewPrompts = draft?.pass1Prompt || draft?.pass2Prompt;
  const hasLegacyPrompt = !hasNewPrompts && draft?.aiPrompt;

  // Fetch default guidelines on mount
  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const result = await getDefaultGuidelines(reportType);
        setDefaultGuidelines(result.guidelines || '');
      } catch (err) {
        console.error('Failed to fetch default guidelines:', err);
      }
    };
    fetchDefaults();
  }, [reportType]);

  // Initialize guidelines with defaults if empty
  useEffect(() => {
    if (!guidelines && defaultGuidelines && !originalGuidelines) {
      setGuidelines(defaultGuidelines);
    }
  }, [defaultGuidelines, guidelines, originalGuidelines]);

  // Reset when switching drafts or when resetKey changes (discard)
  useEffect(() => {
    setGuidelines(draft?.customGuidelines || defaultGuidelines || '');
  }, [defaultGuidelines, draft?.customGuidelines, draft.draftId, resetKey]);

  const guidelinesChanged = guidelines !== (originalGuidelines || defaultGuidelines);

  // Notify parent of change status
  useEffect(() => {
    if (onHasChanges) {
      onHasChanges(guidelinesChanged);
    }
  }, [guidelinesChanged, onHasChanges]);

  // Notify parent of current guidelines value
  useEffect(() => {
    if (onGuidelinesChange) {
      onGuidelinesChange(guidelines);
    }
  }, [guidelines, onGuidelinesChange]);

  const handleResetGuidelines = async () => {
    try {
      setIsLoadingDefaults(true);

      if (defaultGuidelines) {
        setGuidelines(defaultGuidelines);
      } else {
        const result = await getDefaultGuidelines(reportType);
        setDefaultGuidelines(result.guidelines || '');
        setGuidelines(result.guidelines || '');
      }
    } catch (err) {
      console.error('Failed to fetch default guidelines:', err);
      if (onError) onError('Failed to load default guidelines');
    } finally {
      setIsLoadingDefaults(false);
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 text-purple-500" />
            <Typography variant="h6" color="blue-gray">
              AI Settings
            </Typography>
          </div>
          {guidelinesChanged && (
            <Chip
              size="sm"
              value="Unsaved Changes"
              color="amber"
              variant="ghost"
            />
          )}
        </div>

        {/* Pass 1 Prompt - Collapsible */}
        {draft?.pass1Prompt && (
          <div className="mb-3 border rounded-lg overflow-hidden">
            <div
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setShowPass1Prompt(!showPass1Prompt)}
            >
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4 text-purple-500" />
                <Typography variant="small" className="font-medium text-gray-700">
                  Pass 1 Prompt
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt('pass1', draft.pass1Prompt);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={copiedPrompt === 'pass1' ? 'Copied!' : 'Copy prompt'}
                >
                  {copiedPrompt === 'pass1' ? (
                    <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPass1Dialog(true);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="View full prompt"
                >
                  <ArrowsPointingOutIcon className="h-4 w-4 text-gray-500" />
                </button>
                {showPass1Prompt ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </div>
            <Collapse open={showPass1Prompt}>
              <div className="p-3 bg-gray-50 max-h-48 overflow-auto">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                  {draft.pass1Prompt}
                </pre>
              </div>
            </Collapse>
          </div>
        )}

        {/* Pass 2 Prompt - Collapsible */}
        {draft?.pass2Prompt && (
          <div className="mb-4 border rounded-lg overflow-hidden">
            <div
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setShowPass2Prompt(!showPass2Prompt)}
            >
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4 text-blue-500" />
                <Typography variant="small" className="font-medium text-gray-700">
                  Pass 2 Prompt
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt('pass2', draft.pass2Prompt);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={copiedPrompt === 'pass2' ? 'Copied!' : 'Copy prompt'}
                >
                  {copiedPrompt === 'pass2' ? (
                    <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPass2Dialog(true);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="View full prompt"
                >
                  <ArrowsPointingOutIcon className="h-4 w-4 text-gray-500" />
                </button>
                {showPass2Prompt ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </div>
            <Collapse open={showPass2Prompt}>
              <div className="p-3 bg-gray-50 max-h-48 overflow-auto">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                  {draft.pass2Prompt}
                </pre>
              </div>
            </Collapse>
          </div>
        )}

        {/* Legacy AI Prompt - Backward compatibility */}
        {hasLegacyPrompt && (
          <div className="mb-4 border rounded-lg overflow-hidden">
            <div
              className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setShowLegacyPrompt(!showLegacyPrompt)}
            >
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4 text-gray-500" />
                <Typography variant="small" className="font-medium text-gray-700">
                  AI Prompt
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt('legacy', draft.aiPrompt);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={copiedPrompt === 'legacy' ? 'Copied!' : 'Copy prompt'}
                >
                  {copiedPrompt === 'legacy' ? (
                    <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <ClipboardDocumentIcon className="h-4 w-4 text-gray-500" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLegacyDialog(true);
                  }}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title="View full prompt"
                >
                  <ArrowsPointingOutIcon className="h-4 w-4 text-gray-500" />
                </button>
                {showLegacyPrompt ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </div>
            <Collapse open={showLegacyPrompt}>
              <div className="p-3 bg-gray-50 max-h-48 overflow-auto">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                  {draft.aiPrompt}
                </pre>
              </div>
            </Collapse>
          </div>
        )}

        {/* Pass 1 Prompt Dialog */}
        <Dialog
          open={showPass1Dialog}
          handler={() => setShowPass1Dialog(false)}
          size="xl"
          className="max-h-[90vh]"
        >
          <DialogHeader className="flex items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-purple-500" />
              <Typography variant="h5">Pass 1 Prompt</Typography>
            </div>
            <IconButton
              variant="text"
              onClick={() => setShowPass1Dialog(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          </DialogHeader>
          <DialogBody className="overflow-auto max-h-[70vh]">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-4 rounded-lg">
              {draft?.pass1Prompt}
            </pre>
          </DialogBody>
        </Dialog>

        {/* Pass 2 Prompt Dialog */}
        <Dialog
          open={showPass2Dialog}
          handler={() => setShowPass2Dialog(false)}
          size="xl"
          className="max-h-[90vh]"
        >
          <DialogHeader className="flex items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-500" />
              <Typography variant="h5">Pass 2 Prompt</Typography>
            </div>
            <IconButton
              variant="text"
              onClick={() => setShowPass2Dialog(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          </DialogHeader>
          <DialogBody className="overflow-auto max-h-[70vh]">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-4 rounded-lg">
              {draft?.pass2Prompt}
            </pre>
          </DialogBody>
        </Dialog>

        {/* Legacy AI Prompt Dialog */}
        <Dialog
          open={showLegacyDialog}
          handler={() => setShowLegacyDialog(false)}
          size="xl"
          className="max-h-[90vh]"
        >
          <DialogHeader className="flex items-center justify-between border-b">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-gray-500" />
              <Typography variant="h5">AI Prompt</Typography>
            </div>
            <IconButton
              variant="text"
              onClick={() => setShowLegacyDialog(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          </DialogHeader>
          <DialogBody className="overflow-auto max-h-[70vh]">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-4 rounded-lg">
              {draft?.aiPrompt}
            </pre>
          </DialogBody>
        </Dialog>

        {/* Editable Guidelines */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <Typography variant="small" className="font-semibold text-gray-700">
              AI Guidelines
              {guidelinesChanged && (
                <span className="ml-2 text-amber-600 font-normal">(modified)</span>
              )}
            </Typography>
            <Button
              variant="text"
              size="sm"
              onClick={handleResetGuidelines}
              disabled={isLoadingDefaults}
              className="flex items-center gap-1 py-1 px-2 text-xs"
            >
              {isLoadingDefaults ? (
                <Spinner className="h-3 w-3" />
              ) : (
                <ArrowPathIcon className="h-3 w-3" />
              )}
              Reset to Defaults
            </Button>
          </div>

          <textarea
            value={guidelines}
            onChange={(e) => setGuidelines(e.target.value)}
            className={`flex-1 w-full p-3 border rounded-lg font-mono text-xs resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[200px] ${
              guidelinesChanged ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
            }`}
            placeholder="Enter AI guidelines for generating report commentary..."
          />

          <div className="flex items-center justify-between mt-2">
            <Typography variant="small" color="gray" className="text-xs">
              {guidelines.length.toLocaleString()} characters
            </Typography>
          </div>
        </div>

        <Typography variant="small" color="gray" className="mt-4 text-xs">
          Guidelines control how the AI generates commentary for your report. Use the Refresh button in the header to apply changes.
        </Typography>
      </CardBody>
    </Card>
  );
}

export default AISettingsPanel;
